var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { GeneralActions } from '../redux';
import { mealPrepDoneStore } from '../facades/mealPrep/mealPrepDoneStore';
import { StorageKeys } from './IStorage';
/**
 * Creates a centralized logout service that performs complete session cleanup.
 *
 * This service ensures that logout logic is identical whether triggered by:
 * - User clicking logout button
 * - 401 Unauthorized error from API
 *
 * Execution order is CRITICAL to prevent race conditions:
 * 1. Stop the Bleeding: Cancel/Reset Queries (prevents new API calls)
 * 2. External Cleanup: Revoke tokens and reset reminders (awaited)
 * 3. Storage Wipe: Remove all persistent data (awaited - MUST complete before Redux update)
 * 4. UI State Update: Dispatch Redux actions (triggers re-render)
 *
 * @param deps - Required dependencies for logout operations
 * @returns Async function that performs complete logout cleanup
 */
export var createLogoutService = function (deps) {
    return function () { return __awaiter(void 0, void 0, Promise, function () {
        var dispatch, storage, queryClient, authRepo, resetReminder;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    dispatch = deps.dispatch, storage = deps.storage, queryClient = deps.queryClient, authRepo = deps.authRepo, resetReminder = deps.resetReminder;
                    // ============================================
                    // STEP 1: STOP THE BLEEDING
                    // ============================================
                    // Clear entire query cache to prevent stale data from previous account
                    // being served to the next logged-in user before refetch completes
                    queryClient.clear();
                    // ============================================
                    // STEP 2: EXTERNAL CLEANUP
                    // ============================================
                    // Revoke tokens from auth repository (removes tokens from storage and notifies consumers)
                    // Reset reminder state (external service cleanup)
                    // Both operations are awaited to ensure completion before proceeding
                    return [4 /*yield*/, Promise.all([authRepo.revokeToken(), resetReminder()])];
                case 1:
                    // ============================================
                    // STEP 2: EXTERNAL CLEANUP
                    // ============================================
                    // Revoke tokens from auth repository (removes tokens from storage and notifies consumers)
                    // Reset reminder state (external service cleanup)
                    // Both operations are awaited to ensure completion before proceeding
                    _a.sent();
                    // ============================================
                    // STEP 3: STORAGE WIPE
                    // ============================================
                    // Remove all customer-related data from persistent storage
                    // CRITICAL: These MUST complete before Redux dispatch to prevent race condition
                    // If Redux updates first, useIsUserAuthenticated might read old token from storage
                    // The pending redeem codes belong to the session that stored them: leaving
                    // them behind would make the next account resume someone else's redeem flow
                    // (the code is also pre-filled in the input). They are always recoverable
                    // from the `?code=` query param, so wiping them here loses nothing.
                    return [4 /*yield*/, Promise.all([
                            storage.removeItem(StorageKeys.CUSTOMER_CONFIG),
                            storage.removeItem(StorageKeys.CUSTOMER_CONFIG_VERSION),
                            storage.removeItem(StorageKeys.CUSTOMER),
                            storage.removeItem(StorageKeys.USER_TIME_ZONE),
                            storage.removeItem(StorageKeys.MEAL_PREP_DONE),
                            storage.removeItem(StorageKeys.COUPON_CODE_KEY),
                            storage.removeItem(StorageKeys.B2B_CODE_KEY),
                        ])];
                case 2:
                    // ============================================
                    // STEP 3: STORAGE WIPE
                    // ============================================
                    // Remove all customer-related data from persistent storage
                    // CRITICAL: These MUST complete before Redux dispatch to prevent race condition
                    // If Redux updates first, useIsUserAuthenticated might read old token from storage
                    // The pending redeem codes belong to the session that stored them: leaving
                    // them behind would make the next account resume someone else's redeem flow
                    // (the code is also pre-filled in the input). They are always recoverable
                    // from the `?code=` query param, so wiping them here loses nothing.
                    _a.sent();
                    // Reset the in-memory Meal Prep "done" store so the next account doesn't
                    // inherit the previous session's map (storage is wiped just above).
                    mealPrepDoneStore.reset();
                    // ============================================
                    // STEP 4: UI STATE UPDATE
                    // ============================================
                    // Update Redux state to reflect logout
                    // This triggers re-renders and navigation changes
                    // Done LAST to ensure storage is already clean
                    dispatch(GeneralActions.setIsAuthorized(false));
                    dispatch(GeneralActions.setIsPlanCreated(false));
                    dispatch(GeneralActions.setIsCountryCompleted(false));
                    dispatch(GeneralActions.clearCustomerConfig());
                    dispatch(GeneralActions.setUserTimezone(undefined));
                    // Reset registration states that might interfere with new login
                    dispatch(GeneralActions.setIsFirstSignUp(false));
                    dispatch(GeneralActions.setIsMemberRegistryCompleted(undefined));
                    dispatch(GeneralActions.setIsWalkThroughCompleted(true));
                    dispatch(GeneralActions.setIsRedeemCoupon(false));
                    return [2 /*return*/];
            }
        });
    }); };
};
